import React, {
    ReactNode,
    useState,
    useEffect,
    useContext
} from 'react';

// Material UI
import {
    Drawer,
    IconButton,
    Grid
} from '@material-ui/core';

// Components
import Typography from '@components/UI-KIT/Typography';
import WarningTemplate from '@/src/components/UI-KIT/Templates/Warning';
import Button from '@components/UI-KIT/Button';
import Footer from '@components/UI-KIT/Footer';

// Icons
import { Close } from '@/assets/icons';

// Style
import VARIABLES from '@/assets/variables';
import './SideDrawer.scss';

export const SideDrawerContext = React.createContext({
    setPreventClosing: (_preventClosing: boolean) => {},
    handleCloseDrawer: () => {}
});

export const useDrawer = () => {
    const context = useContext(SideDrawerContext);
    if (!context) {
        throw new Error('useDrawer must be used within a SideDrawerContext');
    }

    return context;
};


interface Props {
    closeDrawer: () => void;
    open: boolean;
    title: string | ReactNode;
    children: ReactNode;
    titleIcon?: ReactNode;
    withFooter?: boolean;
    withFooterDouble?: boolean;
    className?: string;
}

const SideDrawer: React.FC<Props> = ({
    closeDrawer,
    open,
    title,
    children,
    titleIcon,
    withFooter = true,
    withFooterDouble = false,
    className
}) => {
    const [ closeDrawerScreen, setCloseDrawerScreen ] = useState(false);
    const [ hideFooter, setHideFooter ] = useState(false);
    const [ preventClosing, setPreventClosing ] = useState(false);

    useEffect(() => {
        setHideFooter(closeDrawerScreen);
    }, [ closeDrawerScreen ]);

    useEffect(() => {
        setCloseDrawerScreen(false);
    }, [ closeDrawer ]);

    useEffect(() => {
        return () => setPreventClosing(true);
    }, [ ]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={() => preventClosing
                ? setCloseDrawerScreen(true)
                : closeDrawer()
            }
            classes={{
                paper: 'drawer-paper ui-Sidedrawer-paper-component',
                root: 'ui-Sidedrawer-root-component'
            }}
        >
            <Grid
                className="ui-Sidedrawer-content"
            >
                <Grid
                    container
                    justify="space-between"
                    alignItems="center"
                    className="ui-Sidedrawer-header"
                >
                    <Grid
                        container
                        justify="space-between"
                    >
                        <Grid
                            container
                            item
                            xs={10}
                        >
                            { titleIcon && (
                                <Grid
                                    item
                                    style={{
                                        marginRight: '.5rem',
                                        display: 'flex'
                                    }}
                                >
                                    {titleIcon}
                                </Grid>
                            )}
                            <Grid
                                item
                            >
                                <Typography
                                    variant="h3"
                                    color="primary-dark"
                                >
                                    {title}
                                </Typography>
                            </Grid>
                        </Grid>
                        <IconButton
                            disableRipple
                            className="ui-Sidedrawer-close-button"
                            onClick={() => {
                                (closeDrawerScreen || !preventClosing)
                                    ? closeDrawer()
                                    : setCloseDrawerScreen(true);
                            }}
                        >
                            <Close
                                color={VARIABLES.palette.secondary_color}
                            />
                        </IconButton>
                    </Grid>
                </Grid>
                <Grid
                    className={`ui-Sidedrawer-children${(withFooter && !hideFooter) ? ' with-footer' : ''}${(withFooterDouble && !hideFooter) ? ' with-footer-double' : ''}${className ? ` ${className}` : ''}`}
                >
                    <SideDrawerContext.Provider
                        value={{
                            handleCloseDrawer: closeDrawer,
                            setPreventClosing
                        }}
                    >
                        <Grid
                            style={{ display: !closeDrawerScreen ? 'block' : 'none' }}
                        >
                            {children}
                        </Grid>
                        <Grid
                            style={{ display: closeDrawerScreen ? 'block' : 'none' }}
                        >
                            <WarningTemplate
                                title="¿Descartar los cambios?"
                                subtitle="Los cambios que hayas registrado se perderán."
                            >
                                <Footer
                                    className="ui-Sidedrawer-warning-template-footer"
                                >
                                    <Grid
                                        item
                                        container
                                        justify="center"
                                        xs={6}
                                    >
                                        <Button
                                            label="Cancelar"
                                            level={2}
                                            onClick={() => setCloseDrawerScreen(false)}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        container
                                        justify="center"
                                        xs={6}
                                    >
                                        <Button
                                            warningColor
                                            label="Descartar"
                                            onClick={closeDrawer}
                                        />
                                    </Grid>
                                </Footer>
                            </WarningTemplate>
                        </Grid>
                    </SideDrawerContext.Provider>
                </Grid>
            </Grid>
        </Drawer>
    );
};

export default SideDrawer;
